/**
 * Task activity timeline — Supabase (with Realtime subscriptions)
 */

import { supabase, TABLES, fromDbList } from './db';
import { TaskTimelineEntry } from '../types';
import { getDisplayNameForUser } from './connections';

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

export function subscribeTaskTimeline(
  taskId: string,
  onUpdate: (entries: TaskTimelineEntry[]) => void,
  onError?: (e: { code?: string; message?: string }) => void
): () => void {
  const load = async () => {
    const { data, error } = await supabase
      .from(TABLES.TASK_TIMELINE_ENTRIES)
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) { onError?.(error); return; }
    onUpdate(fromDbList<TaskTimelineEntry>(data ?? []));
  };

  void load();

  const channel = supabase
    .channel(`timeline-${taskId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.TASK_TIMELINE_ENTRIES, filter: `task_id=eq.${taskId}` },
      () => void load()
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR') onError?.({ message: 'Realtime subscription error' });
    });

  return () => { void supabase.removeChannel(channel); };
}

// ============================================================================
// WRITE
// ============================================================================

export async function addTaskTimelineComment(
  taskId: string,
  authorId: string,
  body: string
): Promise<void> {
  const text = body.trim();
  if (!text) return;
  const authorName = await getDisplayNameForUser(authorId, 'Member');
  const now = new Date().toISOString();
  const { error } = await supabase.from(TABLES.TASK_TIMELINE_ENTRIES).insert({
    task_id: taskId,
    author_id: authorId,
    author_name: authorName,
    body: text,
    entry_type: 'comment',
    created_at: now,
  });
  if (error) throw error;
}

export async function deleteTaskTimelineEntry(entryId: string): Promise<void> {
  const { error } = await supabase.from(TABLES.TASK_TIMELINE_ENTRIES).delete().eq('id', entryId);
  if (error) throw error;
}

// ============================================================================
// AUTO CHANGE LOG
// ============================================================================

const FIELD_LABELS: Record<string, string> = {
  title: 'Title', summary: 'Summary', requirements: 'Requirements',
  acceptanceCriteria: 'Acceptance criteria', priority: 'Priority', status: 'Status',
  urgency: 'Urgency', riskLevel: 'Risk level', environment: 'Environment',
  category: 'Category', impact: 'Impact', repoUrl: 'Repo URL',
  communicationChannel: 'Communication channel', dependencies: 'Dependencies',
  estimatedHours: 'Estimated hours', tags: 'Tags', dueDate: 'Due date',
  startDate: 'Start date', completedAt: 'Completed at',
};

function formatFieldValue(key: string, val: unknown): string {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'string' && (key === 'dueDate' || key === 'startDate' || key === 'completedAt')) {
    try {
      return new Date(val).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return val; }
  }
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  return String(val);
}

export async function addTaskChangeEntry(
  taskId: string,
  authorId: string,
  oldValues: Record<string, unknown>,
  newValues: Record<string, unknown>
): Promise<boolean> {
  const diffs: string[] = [];

  for (const key of Object.keys(FIELD_LABELS)) {
    const oldVal = oldValues[key];
    const newVal = newValues[key];

    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      if (oldVal.join(',') !== newVal.join(',')) {
        diffs.push(`${FIELD_LABELS[key]}: ${formatFieldValue(key, oldVal)} → ${formatFieldValue(key, newVal)}`);
      }
      continue;
    }

    const a = oldVal ?? '';
    const b = newVal ?? '';
    if (String(a) !== String(b)) {
      diffs.push(`${FIELD_LABELS[key]}: ${formatFieldValue(key, oldVal)} → ${formatFieldValue(key, newVal)}`);
    }
  }

  if (diffs.length === 0) return false;

  const authorName = await getDisplayNameForUser(authorId, 'Member');
  const body = `Updated ${diffs.length} field${diffs.length > 1 ? 's' : ''}:\n${diffs.join('\n')}`;
  const now = new Date().toISOString();

  const { error } = await supabase.from(TABLES.TASK_TIMELINE_ENTRIES).insert({
    task_id: taskId,
    author_id: authorId,
    author_name: authorName,
    body,
    entry_type: 'change',
    created_at: now,
  });
  if (error) throw error;
  return true;
}

export async function syncTaskParticipantsForTask(taskId: string, ownerId: string): Promise<void> {
  const { data: reqs, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('tasker_id')
    .eq('task_id', taskId)
    .eq('task_owner_id', ownerId)
    .eq('status', 'accepted');

  if (error) { console.error('syncTaskParticipants:', error); return; }
  if (!reqs || reqs.length === 0) return;

  const now = new Date().toISOString();
  const rows = (reqs as Array<{ tasker_id: string }>).map((r) => ({
    task_id: taskId,
    user_id: r.tasker_id,
    role: 'collaborator',
    added_at: now,
  }));

  const { error: upsertErr } = await supabase
    .from(TABLES.TASK_PARTICIPANTS)
    .upsert(rows, { onConflict: 'task_id,user_id' });
  if (upsertErr) console.error('syncTaskParticipants upsert:', upsertErr);
}
